import { isSemanticModelGeneralization } from "@dataspecer/core-v2/semantic-model/concepts";
import { VISUAL_PROFILE_RELATIONSHIP_TYPE, VISUAL_RELATIONSHIP_TYPE } from "@dataspecer/visual-model";
import { capitalizeFirstLetter, createIdentifier } from "../../util/utils.js";
class VisualEdge {
    constructor(visualEdge, isOutsider) {
        this.visualEdge = visualEdge;
        this.isOutsider = isOutsider;
    }
    visualEdge;
    /**
     * True if edge has one end it outsider node, therefore it was artificially created. Otherwise it comes from visual model.
     * In case when we didn't have any visual model on input, this property can be safely ignored. (It is always false.)
     */
    isOutsider;
}
/**
 * Represents the graph edge.
 */
export class DefaultEdge {
    /**
     * Adds edge to given {@link graph}
     * @param extractedModels used to search for source model of edge - so not needed for non-semantic edges
     * @returns
     */
    static addNewEdgeToGraph(graph, identifier, visualEdge, semanticEdge, sourceIdentifier, targetIdentifier, extractedModels, edgeToAddKey) {
        if (edgeToAddKey === null || edgeToAddKey === undefined) {
            edgeToAddKey = "outgoingRelationshipEdges";
        }
        const reverseEdgeToAddKey = convertOutgoingEdgeTypeToIncoming(edgeToAddKey);
        const mainGraph = graph.mainGraph;
        const source = mainGraph.findNodeInAllNodes(sourceIdentifier);
        const target = mainGraph.findNodeInAllNodes(targetIdentifier);
        if (source === undefined) {
            console.warn("The source node for edge does not exists");
            return;
        }
        else if (target === undefined) {
            console.warn("The target node for edge does not exists");
            return;
        }
        let sourceModelIdentifierForEdge = null;
        if (extractedModels !== null) {
            if (edgeToAddKey === "outgoingClassProfileEdges") {
                sourceModelIdentifierForEdge = source.sourceModelIdentifier ?? null;
            }
            else {
                sourceModelIdentifierForEdge = extractedModels.entities
                    .find(entity => entity.semanticEntity.id === semanticEdge?.id)?.sourceModelIdentifier ?? null;
            }
        }
        const edge = new DefaultEdge(identifier, visualEdge, semanticEdge, edgeToAddKey, graph, source, target, sourceModelIdentifierForEdge);
        const reverseEdge = edge;
        source[edgeToAddKey].push(edge);
        target[reverseEdgeToAddKey].push(reverseEdge);
        return edge;
    }
    constructor(identifier, visualRelationship, semanticEdge, edgeType, sourceGraph, start, end, sourceModelIdentifier) {
        this.sourceGraph = sourceGraph;
        this.isDummy = false;
        this.sourceModelIdentifier = sourceModelIdentifier;
        this.edgeProfileType = convertOutgoingEdgeTypeToEdgeProfileType(edgeType);
        this.edgeType = edgeType;
        this.semanticEntityRepresentingEdge = semanticEdge;
        this.start = start;
        this.end = end;
        this.visualEdge = this.createVisualEdgeBasedOnData(visualRelationship, semanticEdge, this.edgeProfileType, sourceModelIdentifier, start, end);
        this.id = identifier ?? this.visualEdge.visualEdge.id;
        sourceGraph.mainGraph.insertInAllEdges(this);
    }
    sourceGraph;
    sourceModelIdentifier;
    id;
    semanticEntityRepresentingEdge;
    isDummy;
    edgeProfileType;
    isConsideredInLayout = true;
    reverseInLayout = false;
    start;
    end;
    visualEdge;
    edgeType;
    layoutOptions = {};
    convertToDataspecerRepresentation() {
        return this.visualEdge.visualEdge;
    }
    /**
     * The semantic information about represented relationship may be missing
     */
    createNewVisualRelationshipBasedOnEndPoints(start, end, edgeProfileType, sourceModelIdentifier) {
        // TODO Hard to solve by myself - Radstr:
        //      It makes sense to use the cme methods to create the visual entities - Instead of implementing it all again - just define method and call it
        //      ... for example I am not sure the type should cotnain only the VISUAL_RELATIONSHIP_TYPE or also some other type, so for such cases constistency would be nice
        //      But currently it seems sufficient
        if (edgeProfileType === "CLASS-PROFILE") {
            const edgeToReturn = {
                id: createIdentifier(),
                entity: start?.semanticEntityRepresentingNode?.id ?? "",
                type: [VISUAL_PROFILE_RELATIONSHIP_TYPE],
                waypoints: [],
                model: sourceModelIdentifier ?? "",
                visualSource: start.id,
                visualTarget: end.id,
            };
            return edgeToReturn;
        }
        else {
            const edgeToReturn = {
                id: createIdentifier(),
                representedRelationship: "",
                type: [VISUAL_RELATIONSHIP_TYPE],
                waypoints: [],
                model: sourceModelIdentifier ?? "",
                visualSource: start.id,
                visualTarget: end.id,
            };
            return edgeToReturn;
        }
    }
    /**
     * @returns Either returns visual edge if given or creates new one based on semantic data
     */
    createVisualEdgeBasedOnData(visualRelationship, semanticEdge, edgeProfileType, sourceModelIdentifier, start, end) {
        if (visualRelationship !== null) {
            return new VisualEdge(visualRelationship, false);
        }
        else if (semanticEdge !== null) {
            const createdVisualRelationship = createNewVisualRelationshipBasedOnSemanticData(semanticEdge, edgeProfileType, sourceModelIdentifier, start, end);
            return new VisualEdge(createdVisualRelationship, true);
        }
        else {
            return new VisualEdge(this.createNewVisualRelationshipBasedOnEndPoints(start, end, edgeProfileType, sourceModelIdentifier), true);
        }
    }
}
/**
 * @deprecated It probably works, but I am no longer using it anywhere
 */
const checkIfEdgeHasAtLeastOneOutsider = (outsiders, start, end) => {
    return outsiders[start] !== undefined || outsiders[end] !== undefined;
};
/**
 * @deprecated It probably works, but I am no longer using it anywhere
 * @returns Returns true if the relationship is inside the visual model or the model is null.
 */
const isRelationshipInVisualModel = (visualModel, entitiesToLayout, relationshipIdentifier, ends) => {
    if (visualModel === null) {
        return true;
    }
    const isAtLeastOneEndOutsider = checkIfEdgeHasAtLeastOneOutsider(entitiesToLayout.outsiders, ends[0], ends[1]);
    const visualEntity = visualModel.getVisualEntity(relationshipIdentifier);
    const isPresentInVisualModel = visualEntity !== null || isAtLeastOneEndOutsider;
    return isPresentInVisualModel;
};
/**
 * @returns Returns true if the class is inside the visual model or if the model is null.
 */
const isClassInLayoutedEntities = (visualModel, entitiesToLayout, classIdentifier) => {
    if (visualModel === null) {
        return true;
    }
    const visualEntities = visualModel.getVisualEntitiesForRepresented(classIdentifier);
    let isPresentInVisualEntitiesToLayout = false;
    for (const visualEntity of visualEntities) {
        isPresentInVisualEntitiesToLayout = isPresentInVisualEntitiesToLayout || entitiesToLayout.visualEntities.includes(visualEntity.id);
    }
    const isPresentInVisualModel = isPresentInVisualEntitiesToLayout ||
        entitiesToLayout.outsiders[classIdentifier] !== undefined;
    return isPresentInVisualModel;
};
/**
 * @deprecated It probably works, but I am no longer using it anywhere
 * @returns Returns true if both ends of the generalization exists in the visual model
 */
const isGeneralizationInVisualModel = (visualModel, entitiesToLayout, generalization) => {
    if (visualModel === null) {
        return true;
    }
    const isChildPresentInVisualModel = isClassInLayoutedEntities(visualModel, entitiesToLayout, generalization.child);
    const isParentPresentInVisualModel = isClassInLayoutedEntities(visualModel, entitiesToLayout, generalization.parent);
    return isChildPresentInVisualModel && isParentPresentInVisualModel;
};
function convertOutgoingEdgeTypeToEdgeProfileType(outgoingEdgeType) {
    switch (outgoingEdgeType) {
        case "outgoingClassProfileEdges":
            return "CLASS-PROFILE";
        case "outgoingProfileEdges":
            return "EDGE-PROFILE";
        case "outgoingGeneralizationEdges":
        case "outgoingRelationshipEdges":
            return "EDGE";
        default:
            throw new Error(`Invalid OutgoingEdgeType - ${outgoingEdgeType}`);
    }
}
export const getEdgeTypeNameFromEdge = (edge) => {
    if (edge.edgeProfileType === "EDGE-PROFILE") {
        return "outgoingProfileEdges";
    }
    else if (edge.edgeProfileType === "CLASS-PROFILE") {
        return "outgoingClassProfileEdges";
    }
    else if (edge.edgeProfileType === "EDGE") {
        if (isSemanticModelGeneralization(edge.semanticEntityRepresentingEdge)) {
            return "outgoingGeneralizationEdges";
        }
        else {
            return "outgoingRelationshipEdges";
        }
    }
    else {
        throw new Error(`Edge of this ${edge.edgeProfileType} type doesn't exist`);
    }
};
export const convertOutgoingEdgeTypeToIncoming = (outgoingEdgeType) => {
    return "incoming" + capitalizeFirstLetter(outgoingEdgeType.slice("outgoing".length));
};
function createNewVisualRelationshipBasedOnSemanticData(semanticEdge, edgeProfileType, sourceModelIdentifier, start, end) {
    // TODO Hard to solve by myself - Radstr:
    //      It makes sense to use the cme methods to create the visual entities - Instead of implementing it all again - just define method and call it
    //      ... for example I am not sure the type should cotnain only the VISUAL_PROFILE_RELATIONSHIP_TYPE or also some other type, so for such cases constistency would be nice
    //      But currently it seems sufficient
    if (edgeProfileType === "CLASS-PROFILE") {
        const edgeToReturn = {
            id: createIdentifier(),
            entity: start.semanticEntityRepresentingNode.id,
            type: [VISUAL_PROFILE_RELATIONSHIP_TYPE],
            waypoints: [],
            model: sourceModelIdentifier ?? "",
            visualSource: start.id,
            visualTarget: end.id,
        };
        return edgeToReturn;
    }
    const edgeToReturn = {
        id: createIdentifier(),
        type: [VISUAL_RELATIONSHIP_TYPE],
        representedRelationship: semanticEdge.id,
        waypoints: [],
        model: sourceModelIdentifier ?? "",
        visualSource: start.id,
        visualTarget: end.id,
    };
    return edgeToReturn;
}
//# sourceMappingURL=edge.js.map